// MACHINERY V2 (Stroje) — Špecifikácie tab renderers (M2.5).
//
// Spec: data package §3. Reference impl:
// design_handoff_machinery_v3/machinery-profile-tabs.jsx (`SpecRow`,
// `SpecSectionCard`, `SpecsTab`); artboards 1006 (desktop), 1015 (phone).
//
// THE UNIT MATHS IS IMPORTED, NEVER RE-DONE. `convertSpec` (utils/
// unitConversion.ts) owns the 16 imperial + 10 metric maps, the
// `.trim().toLowerCase()` lookup, the unknown-unit pass-through and `smartRound`
// (0dp >=100 / 1dp >=10 / 2dp <10). A numeric row asks it; a text row never
// does. §3: "For a numeric row specValue is NEVER shown."
//
// SECTION NAMES ARE NOT TRANSLATED. They are Techspex English and are rendered
// verbatim — the artboard says so on the toolbar line, and §3 repeats it. Only
// the GROUP labels (Core Parameters / Spindle Data / ...) are ours and
// translated.
//
// EQUIPMENT SECTIONS KEEP THEIR OWN EXPAND STATE. §3 "Actions": equipment
// sections have their own local expand state and Expand-All does NOT drive
// them. That is why `EquipmentSection` holds a `useState` instead of taking an
// `expanded` prop like `SpecSection` does.
//
// The glyph/divider/pill half lives in specChrome.tsx (300-line cap).
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React, { useState } from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";
import { CatalogSpecItem, CatalogSpecSection } from "../../../models/models";
import { convertSpec } from "../../../utils/unitConversion";
import { isStandardEquipment } from "./specSheet";
import { Chevron, CountPill } from "./specChrome";

const SectionShell: React.FC<{
  name: string;
  pill: string;
  open: boolean;
  onToggle: () => void;
  children?: React.ReactNode;
}> = ({ name, pill, open, onToggle, children }) => (
  <div
    style={{
      background: "var(--card)",
      border: "1px solid var(--border)",
      borderRadius: 10,
      boxShadow: "var(--shadow)",
      overflow: "hidden",
      minWidth: 0,
    }}
  >
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 9,
        width: "100%",
        padding: "11px 16px",
        border: 0,
        borderBottom: open ? "1px solid var(--border)" : "none",
        background: "transparent",
        cursor: "pointer",
        font: "inherit",
        textAlign: "left",
        // 44, NEVER 0 — the SAME defect F4 found on TabSelect, found here by
        // that finding's own audit. The below-1280 floor that raises this
        // disclosure matches it on `aria-expanded`
        // (mobile/machinery-v2-touch.scss) — but an INLINE `min-width: 0`
        // outranks a sheet, so the width half never landed and was met only
        // incidentally by the `width: "100%"` above. Truncation is unaffected:
        // it is the inner span's own `minWidth: 0` that does that work, and
        // this button is a block child, not a shrinking flex item.
        // THAT FLOOR IS ROOT-SCOPED SINCE M2.16e, AND THE DIFFERENCE IS THIS
        // COMPONENT'S: while it was scoped to the profile page, this section
        // header stayed 38px on CatalogDetailMobile, which mounts the same
        // spec sheet outside that page — beside a collapse-all the
        // module-wide `.btn` floor had already raised to 44.
        minWidth: 44,
      }}
    >
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: "0.09em",
          textTransform: "uppercase",
          color: "var(--ink)",
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {name}
      </span>
      <CountPill label={pill} />
      <span style={{ flex: 1 }} />
      <Chevron open={open} />
    </button>
    {open && children}
  </div>
);

/** One spec row. Numeric rows go mono + converted; text rows render verbatim. */
export const SpecRow: React.FC<{ spec: CatalogSpecItem; toMetric: boolean; phone?: boolean }> = ({
  spec,
  toMetric,
  phone,
}) => {
  const isNumeric = spec.numericValue !== null && spec.numericValue !== undefined;
  const converted = isNumeric ? convertSpec(spec.numericValue, spec.unit, toMetric) : null;
  const value = isNumeric && converted ? converted.displayValue : spec.specValue;
  const unit = isNumeric && converted ? converted.displayUnit : spec.unit;
  const empty = !value;

  const label = (
    <span
      style={{
        fontSize: 13,
        color: "var(--muted)",
        textTransform: "capitalize",
        lineHeight: 1.45,
        minWidth: 0,
        wordBreak: "break-word",
      }}
    >
      {spec.specKey}
    </span>
  );
  const shown = (
    <span
      style={{
        fontFamily: isNumeric ? "var(--font-mono)" : "inherit",
        fontSize: 13,
        fontWeight: isNumeric ? 600 : 500,
        color: empty ? "var(--faint)" : "var(--ink)",
        fontVariantNumeric: "tabular-nums",
        textAlign: phone ? "left" : "right",
        minWidth: 0,
        wordBreak: "break-word",
      }}
    >
      {empty ? "—" : value}
      {!empty && unit && (
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            fontWeight: 400,
            color: "var(--faint)",
            marginLeft: 4,
          }}
        >
          {unit}
        </span>
      )}
    </span>
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: phone ? "column" : "row",
        alignItems: phone ? "stretch" : "baseline",
        gap: phone ? 2 : 14,
        padding: phone ? "9px 14px" : "8px 16px",
        borderTop: "1px solid var(--border-soft)",
        minWidth: 0,
      }}
    >
      {phone ? (
        <>
          {label}
          {shown}
        </>
      ) : (
        <>
          <span style={{ flex: 1, minWidth: 0 }}>{label}</span>
          <span style={{ flexShrink: 0, maxWidth: "56%" }}>{shown}</span>
        </>
      )}
    </div>
  );
};

/** A regular (key/value) section. Expansion is owned by the tab. */
export const SpecSection: React.FC<{
  section: CatalogSpecSection;
  open: boolean;
  onToggle: () => void;
  toMetric: boolean;
  phone?: boolean;
}> = ({ section, open, onToggle, toMetric, phone }) => {
  const { translate } = useTranslation("myMachinery");
  return (
    <SectionShell
      name={section.sectionName}
      // RAW length, empty keys included — §3 "Counts/relationships".
      pill={translate("myMachinery:specsCount", { count: section.specs.length })}
      open={open}
      onToggle={onToggle}
    >
      {section.specs.map((spec, i) => (
        <SpecRow key={i} spec={spec} toMetric={toMetric} phone={phone} />
      ))}
    </SectionShell>
  );
};

/** Standard / optional equipment: a checklist keyed on specKey. */
export const EquipmentSection: React.FC<{ section: CatalogSpecSection; phone?: boolean }> = ({
  section,
  phone,
}) => {
  const { translate } = useTranslation("myMachinery");
  const [open, setOpen] = useState(true);
  const standard = isStandardEquipment(section.sectionName);
  // §3: the equipment pill counts NON-empty keys, which is < specs.length.
  const items = section.specs.map((s) => s.specKey).filter((k) => k && k.trim().length > 0);
  if (items.length === 0) return null;

  return (
    <SectionShell
      name={section.sectionName}
      pill={translate(
        standard ? "myMachinery:equipIncluded" : "myMachinery:equipOptional",
        { count: items.length }
      )}
      open={open}
      onToggle={() => setOpen((prev) => !prev)}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: phone ? "1fr" : "repeat(auto-fill, minmax(240px, 1fr))",
          gap: "6px 16px",
          padding: "12px 16px",
          minWidth: 0,
        }}
      >
        {items.map((item, i) => (
          <span
            key={i}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 7,
              fontSize: 12.5,
              color: "var(--ink-2)",
              minWidth: 0,
              wordBreak: "break-word",
            }}
          >
            <span
              aria-hidden
              style={{
                color: standard ? "var(--green)" : "var(--mod-accent)",
                flexShrink: 0,
                lineHeight: 1.4,
              }}
            >
              {standard ? "✓" : "+"}
            </span>
            {item}
          </span>
        ))}
      </div>
    </SectionShell>
  );
};
